import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {FormattedDate} from 'react-intl';

import LegacyCache from '@stubhub/legacy-cache';
import {
  computeCalendarState,
  initializeCurrentDate,
  getWeekDayNames,
  getDaysOfWeekRow,
  setCalendarState,
  monthSelection,
  calendar,
} from '@stubhub/react-calendar-helper';

import CalendarCell from './calendar-cell';
import {rightPointer, leftPointer} from './icons';
import {SelectionTypes} from './utils';

import '../styles/calendar-panel.scss';

const CALENDAR_WEEK_CLASSNAME = 'CalendarPanel__CalendarWeek';
const CALENDAR_NAV_DIRECTION_FORWARD = 'Forward';
const CALENDAR_NAV_DIRECTION_BACK = 'Back';
export const DATE_FILTER = 'date-filter';

// Main Calendar Panel Component
const CalendarPanel = (props) => {
  const {
    weekStartDay,
    lastValidDate,
    isBirth,
    allRange,
    isDateActive: isDateActiveProp,
    onselectDate,
    from,
    to,
    selectionType: initialSelectionType,
    selectedYear: initialSelectedYear,
    selectedMonth: initialSelectedMonth,
  } = props;

  // Initialize LegacyCache (previously in constructor)
  /* eslint-disable-next-line */
  const legacyCache = new LegacyCache({
    type: 'session',
    namespace: DATE_FILTER,
  });

  // Generate the weekday labels
  const initialWeekdayLabels = getWeekDayNames(weekStartDay);

  // Initialize dates and derive calendar state
  const initializeDate = initializeCurrentDate(props);
  const initialCalendarState = computeCalendarState(initializeDate);

  // Set up component state using hooks
  const [state, setState] = useState({
    weekStartDay,
    weekdayLabels: initialWeekdayLabels,
    selectionType: initialSelectionType,
    ...initialCalendarState,
  });

  // Equivalent to UNSAFE_componentWillReceiveProps
  useEffect(() => {
    if (initialSelectedYear !== undefined || initialSelectedMonth !== undefined || initialSelectionType !== undefined) {
      setState((prevState) => ({
        ...prevState,
        selectedYear: initialSelectedYear !== undefined ? initialSelectedYear : prevState.selectedYear,
        selectedMonth: initialSelectedMonth !== undefined ? initialSelectedMonth : prevState.selectedMonth,
        selectionType: initialSelectionType !== undefined ? initialSelectionType : prevState.selectionType,
      }));
    }
  }, [initialSelectedYear, initialSelectedMonth, initialSelectionType]);

  const pickDate = (date, selType) => {
    let selectionTypeToUse = selType;
    if (selectionTypeToUse === undefined) {
      selectionTypeToUse = 'from';
    }
    onselectDate && onselectDate(date);
  };

  const buildCalendarCell = (currentDay, selectedYear, selectedMonth) => {
    const todayDate = new Date(Date.now());
    todayDate.setHours(0, 0, 0, 0);

    /**
     * If Day is in Range of current month then add Event Calendar Cards for those Dates.
     */
    const currentDate = new Date(selectedYear, selectedMonth, currentDay);

    let changeColorstart = false;
    let changeColorend = false;
    let changeColor = false;

    if (from) {
      if (currentDate.valueOf() === from.valueOf()) {
        changeColor = true;
      } else {
        changeColor = false;
      }
    }

    let isDateActive = false;
    if (isDateActiveProp && typeof isDateActiveProp === 'function') {
      isDateActive = isDateActiveProp(currentDate);
    }

    let validDate = currentDate.getTime() >= todayDate;
    let highlight = false;
    if (from && to) {
      highlight = from.getTime() <= currentDate.getTime() && to.getTime() >= currentDate.getTime();
      changeColorstart = from.getTime() === currentDate.getTime();
      if (to.getTime() > from.getTime()) {
        changeColorend = to.getTime() === currentDate.getTime();
      }
    }

    if (
      state.selectionType &&
      state.selectionType === SelectionTypes.TO &&
      validDate &&
      currentDate.getTime() <= from.getTime()
    ) {
      validDate = false;
    }

    if (lastValidDate && currentDate.getTime() > new Date(lastValidDate).getTime()) {
      validDate = false;
    }

    // User may want to choose any date
    if (allRange) {
      validDate = true;
    }

    const eventCardParams = {
      date: currentDate,
      validDate,
      changeColorstart,
      changeColorend,
      highlight,
      changeColor,
      selectionType: state.selectionType,
      activeDate: isDateActive,
    };

    const eventCalendarCardComponent = (
      <CalendarCell {...eventCardParams} onselectDate={onselectDate} pickDate={pickDate} />
    );

    return eventCalendarCardComponent;
  };

  const buildEventCalendar = () => {
    /**
     * Number Of Weeks in the Current Month is equal to sum of
     * a) Number of Days in Previous month which spillover from previous month to first weekIndex.
     * b) Number of Days in Current Month.
     */
    const blankCalendarDateCard = <CalendarCell onselectDate={onselectDate} />;

    const {
      weekStartDay: stateWeekStartDay,
      numberOfDaysInCurrentMonth,
      lastDayOfWeekForPreviousMonth,
      firsDayOfCurrentMonth,
      selectedYear,
      selectedMonth,
    } = state;

    const className = 'CalendarPanel__CalendarDay';

    // The calendar function needs context binding, so we create a bound version of buildCalendarCell
    const boundBuildCalendarCell = buildCalendarCell.bind(null);

    const calendarWeekRow = calendar(
      stateWeekStartDay,
      numberOfDaysInCurrentMonth,
      lastDayOfWeekForPreviousMonth,
      firsDayOfCurrentMonth,
      selectedYear,
      selectedMonth,
      blankCalendarDateCard,
      CALENDAR_WEEK_CLASSNAME,
      className,
      boundBuildCalendarCell,
      null
    );

    return calendarWeekRow;
  };

  const handleMonthSelection = (direction) => {
    let {selectedYear, selectedMonth} = state;
    const selection = monthSelection(selectedYear, selectedMonth, direction);
    ({selectedYear, selectedMonth} = selection);

    /**
     * Update the State with the new Selection.
     */
    const {numberOfDaysInCurrentMonth, firsDayOfCurrentMonth, lastDayOfWeekForPreviousMonth} = setCalendarState({
      selectedYear,
      selectedMonth,
    });

    setState((prevState) => ({
      ...prevState,
      selectedYear,
      selectedMonth,
      numberOfDaysInCurrentMonth,
      firsDayOfCurrentMonth,
      lastDayOfWeekForPreviousMonth,
    }));
  };

  const previousMonth = (e) => {
    e.preventDefault();
    handleMonthSelection(CALENDAR_NAV_DIRECTION_BACK);
  };

  const nextMonth = (e) => {
    e.preventDefault();
    handleMonthSelection(CALENDAR_NAV_DIRECTION_FORWARD);
  };

  const {selectedYear, selectedMonth, weekdayLabels} = state;
  const currentSelectedDateObj = new Date(selectedYear, selectedMonth);
  const weekdayLabelRow = getDaysOfWeekRow(CALENDAR_WEEK_CLASSNAME, weekdayLabels);
  const calendarWeekRow = buildEventCalendar();

  const currentDate = new Date(selectedYear, selectedMonth + 1, 1);
  const date = new Date(Date.now());
  const todayDate = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  const previousMonthAllowed = todayDate < currentDate;

  return (
    <div className="CalendarPanel__CalendarContainer">
      <div className="CalendarPanel__CalendarHeader">
        <div className="CalendarPanel__SelectedMonthYear">
          <span className="CalendarPanel__SelectedMonth">
            <FormattedDate value={currentSelectedDateObj} month="long" />
          </span>
          <span className="CalendarPanel__SelectedYear">
            {' '}
            <FormattedDate value={currentSelectedDateObj} year="numeric" />
          </span>
        </div>
        <div className="CalendarPanel__ButtonContainer">
          <button
            type="button"
            className={`CalendarPanel__PreviousMonthButton ${
              previousMonthAllowed || isBirth || allRange ? '' : 'CalendarPanel__PreviousMonthButton--Disable'
            }`}
            onClick={previousMonth}
            aria-label="Previous month"
          >
            {leftPointer({})}
          </button>
          <button type="button" className="CalendarPanel__NextMonthButton" onClick={nextMonth} aria-label="Next month">
            {rightPointer({})}
          </button>
        </div>
      </div>
      <div className="CalendarPanel__CalendarMonth">
        {weekdayLabelRow}
        {calendarWeekRow}
      </div>
    </div>
  );
};

CalendarPanel.propTypes = {
  weekStartDay: PropTypes.number.isRequired,
  lastValidDate: PropTypes.string,
  isBirth: PropTypes.bool,
  allRange: PropTypes.bool,
  isDateActive: PropTypes.func,
  onselectDate: PropTypes.func,
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
  selectionType: PropTypes.string,
  selectedYear: PropTypes.number,
  selectedMonth: PropTypes.number,
};

CalendarPanel.defaultProps = {
  weekStartDay: 0,
  lastValidDate: null,
  isBirth: false,
  allRange: false,
  onselectDate: null,
  from: null,
  to: null,
  selectionType: null,
  selectedYear: undefined,
  selectedMonth: undefined,
};

export default CalendarPanel;
